import React, { useEffect, useRef, useState } from 'react';
import { ClockIcon, CogIcon } from '@heroicons/react/24/outline';
import { formatGateClosureInResourceTz, getResourceTimezoneLabel } from '../../../utils/datetime';
import FilterChip from '../../../components/FilterChip';
import { ForecasterPicker } from '../../../components/ForecasterPicker';
import { ENSEMBLE_MODELS } from '../../../constants/ensembleModels';

// Chip-style filter bar for the Sessions detail, matching the main chart
// toolbar. The day is a native date input (own calendar — no extra popover);
// Horizon and Gate are chips with inline option popovers (one open at a time).
// Gate shows only when a horizon has more than one gate (rolling intraday), and
// each option is labelled with its closure date, time and CET/CEST. The Forecasters
// picker is shown only when forecasters are supplied (admin/MO view).
export const SessionFilters = ({
  selectedDate,
  maxDate,
  onDateChange,
  horizonOptions = [],
  horizonKey,
  onHorizonChange,
  gateOptions = [],
  selectedChallengeId,
  onGateChange,
  forecasters = [],
  selectedForecasterIds,
  onForecastersChange,
  // Ensemble strategy picker (admin / market owner only).
  model,
  onModelChange,
  showModelPicker = false,
}) => {
  const [open, setOpen] = useState(null); // 'horizon' | 'gate' | 'model' | null
  const barRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event) => {
      if (barRef.current && !barRef.current.contains(event.target)) setOpen(null);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  // Gate label: actual closure date + time in the resource timezone with its
  // own CET/CEST (e.g. "May 20 · 20:00 CEST"), matching the left panel. Each
  // option is labelled at its instant since a horizon's gates can straddle DST.
  const gateText = (gate) => {
    const closure = formatGateClosureInResourceTz(gate?.gate_closure);
    if (!closure) return '—';
    const tz = getResourceTimezoneLabel(undefined, new Date(gate.gate_closure));
    return `${closure.date} · ${closure.time} ${tz}`;
  };

  const horizonLabel = horizonOptions.find((o) => o.key === horizonKey)?.label || '—';
  const modelShort = ENSEMBLE_MODELS.find((m) => m.value === model)?.short || model || '—';
  const selectedGate = gateOptions.find((g) => g.challenge_id === selectedChallengeId) || null;
  const gateLabel = selectedGate ? gateText(selectedGate) : '—';

  const optionClass = (active) =>
    `w-full text-left px-2 py-1.5 rounded text-sm ${
      active
        ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold'
        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;
  const popoverClass =
    'absolute left-0 z-50 mt-2 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-1.5';

  return (
    <div ref={barRef} className="flex flex-wrap items-center gap-2">
      {/* Day — native date input (its own calendar; no extra popover) */}
      <input
        type="date"
        aria-label="Target day"
        value={selectedDate || ''}
        max={maxDate || undefined}
        onChange={(event) => onDateChange(event.target.value)}
        className="h-8 flex-shrink-0 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-2.5 text-xs font-medium text-gray-900 dark:text-gray-100 hover:border-brand-300 transition-colors"
      />

      {/* Horizon */}
      {horizonOptions.length > 0 && (
        <div className="relative">
          <FilterChip
            icon={ClockIcon}
            label="Horizon"
            value={horizonLabel}
            active={open === 'horizon'}
            onClick={() => setOpen(open === 'horizon' ? null : 'horizon')}
          />
          {open === 'horizon' && (
            <div className={`${popoverClass} w-44`}>
              {horizonOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={optionClass(option.key === horizonKey)}
                  onClick={() => {
                    onHorizonChange(option.key);
                    setOpen(null);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gate closure — only when the horizon has multiple gates. Each option
          shows its closure date, time and CET/CEST (see gateText). */}
      {gateOptions.length > 1 && (
        <div className="relative">
          <FilterChip
            icon={ClockIcon}
            label="Gate"
            value={gateLabel}
            active={open === 'gate'}
            onClick={() => setOpen(open === 'gate' ? null : 'gate')}
          />
          {open === 'gate' && (
            <div className={`${popoverClass} w-48 max-h-64 overflow-y-auto`}>
              {gateOptions.map((gate) => (
                <button
                  key={gate.challenge_id}
                  type="button"
                  className={optionClass(gate.challenge_id === selectedChallengeId)}
                  onClick={() => {
                    onGateChange(gate.challenge_id);
                    setOpen(null);
                  }}
                >
                  {gateText(gate)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ensemble strategy — single-select (admin / market owner only).
          Switching it refetches the target-day forecasts for that strategy. */}
      {showModelPicker && (
        <div className="relative">
          <FilterChip
            icon={CogIcon}
            label="Model"
            value={modelShort}
            active={open === 'model'}
            onClick={() => setOpen(open === 'model' ? null : 'model')}
          />
          {open === 'model' && (
            <div className={`${popoverClass} w-56`}>
              {ENSEMBLE_MODELS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={optionClass(m.value === model)}
                  onClick={() => {
                    onModelChange?.(m.value);
                    setOpen(null);
                  }}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span>{m.label}</span>
                    <span className="text-gray-400 dark:text-gray-500 text-xs">{m.short}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Forecasters multi-select (admin / market owner only) */}
      {forecasters.length > 0 && (
        <ForecasterPicker
          forecasters={forecasters}
          selectedIds={selectedForecasterIds}
          onChange={onForecastersChange}
          open={open === 'forecasters'}
          onOpenChange={(v) => setOpen(v ? 'forecasters' : null)}
        />
      )}
    </div>
  );
};
